import { useEffect, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { SidebarLeftIcon, SidebarRightIcon } from "@hugeicons/core-free-icons";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NavMain } from "./NavMain";
import { NavProjects } from "./NavProjects";
import { NavRecents } from "./NavRecents";
import { NavFeedback } from "./NavFeedback";
import { NavUser } from "./NavUser";
import { SidebarCreditsWidget } from "@/components/billing/SidebarCreditsWidget";
import logoLight from "@/assets/png/logo.png";

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { toggleSidebar, state } = useSidebar();
  const isCollapsed = state === "collapsed";

  // Delay hover effect until the 200ms sidebar transition finishes
  const [isFullyCollapsed, setIsFullyCollapsed] = useState(false);
  useEffect(() => {
    if (isCollapsed) {
      const timer = setTimeout(() => setIsFullyCollapsed(true), 200);
      return () => clearTimeout(timer);
    } else {
      setIsFullyCollapsed(false);
    }
  }, [isCollapsed]);

  return (
    <Sidebar variant="inset" collapsible="icon" data-tour="sidebar" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground hover:bg-transparent dark:hover:bg-transparent"
            >
              {/* Logo + toggle icon with fade transition on hover */}
              <div className="relative flex aspect-square size-8 items-center justify-center">
                {/* Logo — fades out on hover when collapsed */}
                <div
                  className={`text-sidebar-primary-foreground flex items-center justify-center rounded-lg transition-opacity duration-200 ${isFullyCollapsed ? "group-hover/sidebar-inner:opacity-0" : ""}`}
                >
                  <img
                    src={logoLight}
                    alt="Rayloop AI"
                    className="size-7 rounded-sm dark:invert"
                  />
                </div>

                {/* Toggle icon — fades in on hover when collapsed */}
                {isFullyCollapsed && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSidebar();
                        }}
                        className="cursor-pointer text-sidebar-foreground hover:bg-sidebar-accent dark:hover:bg-surface-raised-hover absolute inset-0 flex items-center justify-center rounded-md opacity-0 transition-all duration-200 group-hover/sidebar-inner:opacity-100"
                      >
                        <HugeiconsIcon
                          icon={SidebarRightIcon}
                          className="size-4"
                          strokeWidth={2}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Open sidebar</TooltipContent>
                  </Tooltip>
                )}
              </div>

              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">Rayloop AI</span>
                <span className="text-muted-foreground truncate text-xs">
                  Workspace
                </span>
              </div>

              {/* Toggle button — visible when expanded */}
              {!isCollapsed && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSidebar();
                      }}
                      className="cursor-pointer text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent dark:hover:bg-surface-raised-hover ml-auto flex size-6 items-center justify-center rounded-md transition-colors"
                    >
                      <HugeiconsIcon
                        icon={SidebarLeftIcon}
                        className="size-4"
                        strokeWidth={2}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Close sidebar</TooltipContent>
                </Tooltip>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <NavMain />
        <NavProjects />
        <NavRecents />
      </SidebarContent>

      <SidebarFooter>
        <NavFeedback />
        <SidebarCreditsWidget />
        <NavUser />
      </SidebarFooter>

      {/* <SidebarRail /> */}
    </Sidebar>
  );
}
